import { useEffect, useState, useRef } from "react";
import { Typography, Row, Button, Checkbox, Col, Collapse, Radio, Badge } from "antd";
import axios from "axios";
import { Sector } from "@bindeleddet/core/types";
import { StyledCard } from "./style";

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface Props {
  searchTerm: string;
  sectorsChecked: number[];
  yearLevelChecked: number[];
  deadlineChecked: string;
  jobTypeChecked: string;
  locationsChecked: string[];
  publishedThisWeekChecked: boolean;
  handleSearch: (value: string) => void;
  toggleCheckedSectors: (id: number) => void;
  toggleCheckedYearLevel: (value: number) => void;
  toggleCheckedDeadline: (value: string) => void;
  toggleCheckedJobType: (value: string) => void;
  toggleCheckedLocations: (value: string) => void;
  togglePublishedThisWeek: () => void;
  onFilterReset: () => void;
}

// TODO Everything works but clean up this code. Mayby use ant design form insted with useWatch ant design hook?
const JobPostingsFilter: React.FC<Props> = ({
  searchTerm,
  sectorsChecked,
  yearLevelChecked,
  deadlineChecked,
  locationsChecked,
  jobTypeChecked,
  publishedThisWeekChecked,
  handleSearch,
  toggleCheckedSectors,
  toggleCheckedDeadline,
  toggleCheckedJobType,
  toggleCheckedYearLevel,
  toggleCheckedLocations,
  togglePublishedThisWeek,
  onFilterReset,
}) => {
  const deadlines = [
    { value: "", label: "Alle frister" },
    { value: "Siste frist", label: "Siste frist" },
    { value: "Under 3 døgn", label: "Under 3 døgn" },
    { value: "Innen en uke", label: "Innen en uke" },
  ];
  const locations = ["Oslo", "Trondheim", "Bergen", "Stavanger", "Kristiansand", "Trømsø", "Annet"];
  const [sectors, setSectors] = useState<Sector[]>([]);
  const yearLevels = [
    { value: 0, string: "Alle årstrinn" },
    { value: 1, string: "1." },
    { value: 2, string: "2." },
    { value: 3, string: "3." },
    { value: 4, string: "4." },
    { value: 5, string: "5." },
  ];
  const jobTypes = [
    "Alle stillingstyper",
    "Fast stilling",
    "Internship",
    "Trainee",
    "Diplomoppgave",
    "Deltid",
    "Annet",
  ];

  const hasMounted = useRef(false);

  useEffect(() => {
    axios.get<Sector[]>("/companies/sectors/").then(response => {
      setSectors(response.data);
    });
  }, []);

  // Load saved filters from local storage
  useEffect(() => {
    if (hasMounted.current) return;

    const savedFilters = JSON.parse(localStorage.getItem("jobFilters") || "{}");
    if (savedFilters) {
      if (savedFilters.sectorsChecked) savedFilters.sectorsChecked.forEach((id: number) => toggleCheckedSectors(id));
      if (savedFilters.yearLevelChecked?.length) toggleCheckedYearLevel(savedFilters.yearLevelChecked[0]);
      if (savedFilters.deadlineChecked) toggleCheckedDeadline(savedFilters.deadlineChecked);
      if (savedFilters.locationsChecked)
        savedFilters.locationsChecked.forEach((location: string) => toggleCheckedLocations(location));
      if (savedFilters.jobTypeChecked) toggleCheckedJobType(savedFilters.jobTypeChecked);
      if (savedFilters.publishedThisWeekChecked) togglePublishedThisWeek();
      if (savedFilters.searchTerm) handleSearch(savedFilters.searchTerm);
    }

    hasMounted.current = true;
  }, [
    toggleCheckedSectors,
    toggleCheckedYearLevel,
    toggleCheckedDeadline,
    toggleCheckedLocations,
    toggleCheckedJobType,
    togglePublishedThisWeek,
    handleSearch,
  ]);

  // Save filters to local storage
  useEffect(() => {
    const filters = {
      sectorsChecked,
      yearLevelChecked,
      deadlineChecked,
      jobTypeChecked,
      locationsChecked,
      publishedThisWeekChecked,
      searchTerm,
    };
    localStorage.setItem("jobFilters", JSON.stringify(filters));
  }, [
    sectorsChecked,
    yearLevelChecked,
    deadlineChecked,
    jobTypeChecked,
    locationsChecked,
    publishedThisWeekChecked,
    searchTerm,
  ]);

  return (
    <>
      <StyledCard bodyStyle={{ padding: "2em" }}>
        <Collapse ghost expandIconPosition="end">
          {/* Job Type */}
          <Panel
            header={
              <Badge color="#91CA91" dot={jobTypeChecked !== "Alle stillingstyper"}>
                <Title level={5}>Stillingstype</Title>
              </Badge>
            }
            key="1"
          >
            <Row>
              <Radio.Group
                onChange={e => toggleCheckedJobType(e.target.value)}
                value={jobTypeChecked}
                style={{ width: "100%" }}
                defaultValue="Alle stillingstyper"
              >
                <Row>
                  {jobTypes.map(type => {
                    return (
                      <Col xs={24} sm={8} md={24} lg={24} key={type}>
                        <Radio key={type} value={type}>
                          <Text>{type}</Text>
                        </Radio>
                      </Col>
                    );
                  })}
                </Row>
              </Radio.Group>
            </Row>
          </Panel>

          {/* Sector */}
          <Panel
            header={
              <Badge color="#91CA91" dot={sectorsChecked.length > 0}>
                <Title level={5}>Bransje</Title>
              </Badge>
            }
            key="2"
          >
            <Row>
              {sectors.map(sector => {
                if (!sector.name) return null;
                return (
                  <Col xs={12} sm={6} md={24} key={sector.id}>
                    <Checkbox
                      checked={sectorsChecked.includes(sector.id)}
                      onChange={() => toggleCheckedSectors(sector.id)}
                    >
                      <Text>{sector.name}</Text>
                    </Checkbox>
                  </Col>
                );
              })}
            </Row>
          </Panel>

          {/* Work Location */}
          <Panel
            header={
              <Badge color="#91CA91" dot={locationsChecked.length > 0}>
                <Title level={5}>Arbeidssted</Title>
              </Badge>
            }
            key="3"
          >
            <Row>
              {locations.map(location => {
                return (
                  <Col xs={12} sm={6} md={24} key={location}>
                    <Checkbox
                      checked={locationsChecked.includes(location)}
                      onChange={() => toggleCheckedLocations(location)}
                    >
                      <Text>{location}</Text>
                    </Checkbox>
                  </Col>
                );
              })}
            </Row>
          </Panel>
        </Collapse>

        {/* Year Level */}
        <Row>
          <Col span={24} style={{ paddingTop: "6px" }}>
            <Badge color="#91CA91" dot={yearLevelChecked.length > 0}>
              <Title level={5}>Årstrinn</Title>
            </Badge>
          </Col>
          <Col span={24}>
            <Radio.Group
              onChange={e => toggleCheckedYearLevel(e.target.value)}
              value={yearLevelChecked.length > 0 ? yearLevelChecked[0] : 0}
              style={{ width: "100%" }}
            >
              <Row>
                {yearLevels.map(year => {
                  return (
                    <Col key={year.value}>
                      <Radio key={year.value} value={year.value}>
                        <Text>{year.string}</Text>
                      </Radio>
                    </Col>
                  );
                })}
              </Row>
            </Radio.Group>
          </Col>
        </Row>

        {/* Published */}
        <Row>
          <Col span={24} style={{ paddingTop: "6px" }}>
            <Badge color="#91CA91" dot={publishedThisWeekChecked}>
              <Title level={5}>Publisert</Title>
            </Badge>
          </Col>
          <Col span={24}>
            <Checkbox
              checked={publishedThisWeekChecked}
              onChange={() => {
                togglePublishedThisWeek();
              }}
            >
              Publisert denne uken
            </Checkbox>
          </Col>
        </Row>

        {/* Deadline */}
        <Row>
          <Col span={24} style={{ paddingTop: "6px" }}>
            <Badge color="#91CA91" dot={deadlineChecked !== ""}>
              <Title level={5}>Søknadsfrist</Title>
            </Badge>
          </Col>
          <Col span={24}>
            <Radio.Group
              onChange={e => toggleCheckedDeadline(e.target.value)}
              value={deadlineChecked}
              style={{ width: "100%" }}
            >
              <Row>
                {deadlines.map(deadline => {
                  return (
                    <Col xs={24} sm={8} md={24} lg={24} key={deadline.value}>
                      <Radio key={deadline.value} value={deadline.value}>
                        {deadline.label}
                      </Radio>
                    </Col>
                  );
                })}
              </Row>
            </Radio.Group>
          </Col>
        </Row>

        {/* Reset filter button */}
        <Row style={{ paddingTop: "2em" }} justify="center">
          <Button danger block onClick={onFilterReset}>
            Nullstill filtrering
          </Button>
        </Row>
      </StyledCard>
    </>
  );
};

export default JobPostingsFilter;
